import { Button, Fade, Modal, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useFormik } from 'formik';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Yup from 'yup';

import TwoFactorAuthenticationActivatedModal from './twoFactorAuthenticationActivated';

import { blueGradientButton } from '../../../assets/styles/blueGradientButton.style';
import googleAuthenticatorIcon from '../../../assets/svg/googleAuthenticatorIcon.svg';
import modalExitIcon from '../../../assets/svg/modalExitIcon.svg';
import verifyTheCodeIcon from '../../../assets/svg/verifyTheCodeIcon.svg';
import { StepOneModalProps } from '../../../interfaces/ModalProps';
import RESTAPIError from '../../../interfaces/RESTAPIError';
import googleTOTPService from '../../../services/googleTotpService';

const TwoFactorAuthenticationModal = ({
  open,
  handleClose,
  useFlag,
  qrCodeLink,
}: StepOneModalProps) => {
  const [isStepTwoModalOpen, setIsStepTwoModalOpen] = useState(false);
  // const [QRCodeLink, setQRCodeLink] = useState('');
  const [backupCode, setBackupCode] = useState('');
  // const { createToastNotification } = useToastNotificationContext();

  const intl = useIntl();

  const openStepTwoModal = () => {
    setIsStepTwoModalOpen(true);
  };

  const closeStepTwoModal = () => {
    setIsStepTwoModalOpen(false);
  };

  const sixDigitCodeForm = useFormik({
    initialValues: {
      sixDigitCode: '',
      submit: null,
    },
    validationSchema: Yup.object({
      sixDigitCode: Yup.string().min(
        6,
        intl.formatMessage({ defaultMessage: 'Invalid Code' }),
      ),
    }),
    onSubmit: async (values, helpers) => {
      if (useFlag == 'customer') {
        try {
          const response =
            await googleTOTPService.enableCustomerGoogleTOTPService(
              values.sixDigitCode,
            );

          setBackupCode(response.data.backup_code);
          handleClose();
          openStepTwoModal();
        } catch (error) {
          helpers.setStatus({ success: false });
          const customError = error as RESTAPIError;
          const errorMessage =
            customError.message ||
            intl.formatMessage({ defaultMessage: 'Error with 6 Digit Code' });
          helpers.setErrors({ submit: errorMessage });
          helpers.setSubmitting(false);
        }
      } else if (useFlag == 'whistleblower') {
        try {
          const response =
            await googleTOTPService.enableWhistleblowerGoogleTOTPService(
              values.sixDigitCode,
            );

          setBackupCode(response.data.backup_code);
          handleClose();
          openStepTwoModal();
        } catch (error) {
          helpers.setStatus({ success: false });
          const customError = error as RESTAPIError;
          const errorMessage =
            customError.message ||
            intl.formatMessage({ defaultMessage: 'Error with 6 Digit Code' });
          helpers.setErrors({ submit: errorMessage });
          helpers.setSubmitting(false);
        }
      }
    },
  });

  return (
    <>
      <Modal open={open} onClose={() => {}} closeAfterTransition>
        <Fade in={open}>
          <Box
            sx={{
              position: 'absolute' as const,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '605px',
              height: '702px',
              bgcolor: 'rgba(255, 255, 255, 1)',
              borderRadius: '6px',
              boxShadow: 24,
              p: '32px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Add your modal content here */}
            <Button
              onClick={handleClose}
              sx={{
                width: '28px',
                height: '28px',
                position: 'absolute',
                right: '1px',
              }}
            >
              <img src={modalExitIcon} alt='exit-icon'></img>
            </Button>

            <Typography
              sx={{
                fontFamily: 'Schibsted Grotesk',
                fontWeight: 500,
                fontSize: '18px',
              }}
            >
              <FormattedMessage defaultMessage='Enable Two-Factor Authentication' />
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Schibsted Grotesk',
                fontWeight: 400,
                fontSize: '14px',
                color: 'rgba(135, 135, 135, 1)',
              }}
            >
              <FormattedMessage defaultMessage='Safeguard your account in 3 steps:' />
            </Typography>

            {/* Step 1 Box */}
            <Box sx={{ width: '505px', height: '142px', mt: '20px' }}>
              <Typography
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: 'rgba(135, 135, 135, 1)',
                }}
              >
                <FormattedMessage defaultMessage='Step 1' />
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                {/* Authenticator QR Code needs to be dynamic with the one returned by the API */}
                <img
                  src={googleAuthenticatorIcon}
                  alt='google-auth-icon'
                  style={{
                    width: '112px',
                    height: '112px',
                    marginLeft: '22px',
                  }}
                ></img>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    ml: '38px',
                    mt: '12px',
                  }}
                >
                  {' '}
                  <Typography
                    sx={{
                      fontFamily: 'Schibsted Grotesk',
                      fontWeight: 600,
                      fontSize: '16px',
                    }}
                  >
                    <FormattedMessage defaultMessage='Get an authenticator app' />
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Schibsted Grotesk',
                      fontWeight: 500,
                      fontSize: '14px',
                      color: 'rgba(135, 135, 135, 1)',
                    }}
                  >
                    <FormattedMessage
                      defaultMessage='Download and install <b>Google Authenticator</b> on your mobile device.'
                      values={{
                        b: (chunks) => (
                          <b
                            style={{
                              fontFamily: 'Schibsted Grotesk',
                              fontWeight: 500,
                              color: 'black',
                            }}
                          >
                            {chunks}
                          </b>
                        ),
                      }}
                    />
                  </Typography>
                </Box>
              </Box>
            </Box>

            <hr
              style={{
                backgroundColor: 'rgba(214, 214, 214, 1)',
                width: '536px',
                height: '1px',
                marginTop: '20px',
                marginBottom: '20px',
              }}
            />

            {/* Step 2 Box */}
            <Box sx={{ width: '505px', height: '142px' }}>
              <Typography
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: 'rgba(135, 135, 135, 1)',
                }}
              >
                <FormattedMessage defaultMessage='Step 2' />
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
             
                <QRCodeSVG
                  value={qrCodeLink}
                  size={140}
                  bgColor={'#ffffff'}
                  fgColor={'#000000'}
                  level={'L'}
                  includeMargin={false}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    ml: '24px',
                    mt: '12px',
                  }}
                >
                  {' '}
                  <Typography
                    sx={{
                      fontFamily: 'Schibsted Grotesk',
                      fontWeight: 600,
                      fontSize: '16px',
                    }}
                  >
                    <FormattedMessage defaultMessage='Scan the QR Code with authenticator app' />
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Schibsted Grotesk',
                      fontWeight: 500,
                      fontSize: '14px',
                      color: 'rgba(135, 135, 135, 1)',
                    }}
                  >
                    <FormattedMessage
                      defaultMessage='Open the app and <b>scan the QR Code</b> using your mobile device.'
                      values={{
                        b: (chunks) => (
                          <b
                            style={{
                              fontFamily: 'Schibsted Grotesk',
                              fontWeight: 500,
                              color: 'black',
                            }}
                          >
                            {chunks}
                          </b>
                        ),
                      }}
                    />
                  </Typography>
                </Box>
              </Box>
            </Box>

            <hr
              style={{
                backgroundColor: 'rgba(214, 214, 214, 1)',
                width: '536px',
                height: '1px',
                marginTop: '40px',
                marginBottom: '40px',
              }}
            />

            {/* Step 3 Box */}
            <Box sx={{ width: '505px', height: '142px' }}>
              <Typography
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: 'rgba(135, 135, 135, 1)',
                }}
              >
                <FormattedMessage defaultMessage='Step 3' />
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <img
                  src={verifyTheCodeIcon}
                  alt='qr-code'
                  style={{
                    width: '100px',
                    height: '112px',
                    marginLeft: '28px',
                  }}
                ></img>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    ml: '43px',
                    mt: '12px',
                  }}
                >
                  {' '}
                  <Typography
                    sx={{
                      fontFamily: 'Schibsted Grotesk',
                      fontWeight: 600,
                      fontSize: '16px',
                    }}
                  >
                    <FormattedMessage defaultMessage='Verify the code' />
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Schibsted Grotesk',
                      fontWeight: 500,
                      fontSize: '14px',
                      color: 'rgba(135, 135, 135, 1)',
                    }}
                  >
                    <FormattedMessage
                      defaultMessage='Enter the generated <b>6-digit code</b> from your authenticator app.'
                      values={{
                        b: (chunks) => (
                          <b
                            style={{
                              fontFamily: 'Schibsted Grotesk',
                              fontWeight: 500,
                              color: 'black',
                            }}
                          >
                            {chunks}
                          </b>
                        ),
                      }}
                    />
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      width: '369px',
                      height: '46px',
                      mt: '16px',
                    }}
                  >
                    <form noValidate onSubmit={sixDigitCodeForm.handleSubmit}>
                      <TextField
                        hiddenLabel
                        name='sixDigitCode'
                        id='six-digit-code'
                        placeholder='000 000'
                        // fullWidth
                        onBlur={sixDigitCodeForm.handleBlur}
                        onChange={sixDigitCodeForm.handleChange}
                        value={sixDigitCodeForm.values.sixDigitCode}
                        variant='filled'
                        sx={{
                          width: '240px',
                          height: '46px',
                          ...{
                            '& .MuiFilledInput-root:after': {
                              border: 'none',
                            },
                            '& .MuiFilledInput-root:before': {
                              borderBottom: 'none',
                            },
                            '& .MuiFilledInput-root': {
                              backgroundColor: 'rgba(0, 0, 0, 0.03)',
                            },
                            '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before':
                              {
                                borderBottom: 'none',
                              },
                          },
                          ...(sixDigitCodeForm.values.sixDigitCode && {
                            '& .MuiFilledInput-root': {
                              backgroundColor: '#FFFFFF',
                            },
                            '& .MuiFormLabel-root': {
                              fontFamily: 'Schibsted Grotesk',
                              fontSize: '14px',
                              fontWeight: 400,
                              textAlign: 'left',
                              color: '#000000',
                            },
                          }),
                        }}
                        InputProps={{
                          style: {
                            border: sixDigitCodeForm.values.sixDigitCode
                              ? '1px solid rgba(0, 0, 0, 0.5)'
                              : '',
                            borderRadius: '6px',
                            overflow: 'hidden',
                          },
                        }}
                        inputProps={{
                          maxLength: 6, // Set the maximum character limit to 6
                        }}
                      />
                      {sixDigitCodeForm.errors.submit && (
                        <Typography color='error' variant='body2' marginTop={1}>
                          {sixDigitCodeForm.errors.submit}
                        </Typography>
                      )}

                      <Button
                        variant='text'
                        fullWidth
                        type='submit'
                        sx={{
                          ...blueGradientButton,
                          width: '97px',
                          height: '38px',
                          color: 'black',
                          ml: '12px',
                          mt: '4px',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Schibsted Grotesk',
                            fontWeight: 600,
                            fontSize: '14px',
                            textTransform: 'none',
                          }}
                        >
                          <FormattedMessage defaultMessage='Verify' />
                        </Typography>
                      </Button>
                    </form>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>
      <TwoFactorAuthenticationActivatedModal
        open={isStepTwoModalOpen}
        handleClose={closeStepTwoModal}
        backupCode={backupCode}
      />
    </>
  );
};

export default TwoFactorAuthenticationModal;
